import React, { useEffect, useState } from "react";
import DataGrid from "./DataGrid/DataGrid.tsx";
import { PolicyReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";
import moment from "moment";

interface PoliciesReportDataGridProps {
  policies: any[];
  isLoading: boolean;
  selectedPolicies: [string, number][];
  setSelectedPolicies: React.Dispatch<React.SetStateAction<[string, number][]>>;
  formState?: {
    fromDate: string;
    toDate: string;
    company: string;
    policyType: string;
    agent: string;
    selectedOffices: string[];
    paymentPlace: string;
    shouldIncludeAnnulledPolicies: number;
    reportNumber: string;
    reportDate: string;
  };
  totals?: {
    totalPremiumAmount: string;
    totalCommissionAmount: string;
    reportSum: string;
  };
  labelMaps?: {
    agents: { [key: string]: string };
    companies: { [key: string]: string };
    offices: { [key: string]: string };
    policyTypes: { [key: string]: string };
    paymentPlaces: { [key: string]: string };
  };
}

const SEPARATOR = ",";

const PoliciesReportDataGrid: React.FC<PoliciesReportDataGridProps> = ({
  policies,
  selectedPolicies,
  setSelectedPolicies,
  isLoading,
  formState,
  totals,
  labelMaps,
}) => {
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [pendingSelectAll, setPendingSelectAll] = useState(false);

  const printReportMetadata = [
    { label: "От дата", value: formState?.fromDate ?? "" },
    { label: "До дата", value: formState?.toDate ?? "" },
    {
      label: "ЗК",
      value:
        labelMaps?.companies[formState?.company ?? ""] ??
        formState?.company ??
        "",
    },
    {
      label: "Вид Полица",
      value:
        labelMaps?.policyTypes[formState?.policyType ?? ""] ??
        formState?.policyType ??
        "",
    },
    {
      label: "Агент",
      value:
        labelMaps?.agents[formState?.agent ?? ""] ?? formState?.agent ?? "",
    },
    {
      label: "Офис",
      value: (formState?.selectedOffices ?? [])
        .map((id) => labelMaps?.offices[id] ?? id)
        .join("; "),
    },
    {
      label: "Място на плащане",
      value:
        labelMaps?.paymentPlaces[formState?.paymentPlace ?? ""] ??
        formState?.paymentPlace ??
        "",
    },
    {
      label: "Вкл. анулирани",
      value: formState?.shouldIncludeAnnulledPolicies === 1 ? "Да" : "Не",
    },
  ];

  const printReportFooterMetadata = [
    {
      label: "Обща сума на премиите",
      value: `€${totals?.totalPremiumAmount ?? "0.00"}`,
    },
    {
      label: "Общ комисион",
      value: `€${totals?.totalCommissionAmount ?? "0.00"}`,
    },
    {
      label: "Сума за отчет",
      value: `€${totals?.reportSum ?? "0.00"}`,
    },
    {
      label: "Номер на отчет",
      value: formState?.reportNumber ?? "",
    },
    {
      label: "Дата на отчет",
      value: moment(formState?.reportDate).format("DD/MM/YYYY") ?? "",
    },
  ];

  useEffect(() => {
    if (policies.length > 0) {
      const allSelected = policies.every((policy) =>
        selectedPolicies.some(
          ([polId, payNum]) =>
            polId === policy[PolicyReportMappingEnum.Pol_ID] &&
            payNum === policy[PolicyReportMappingEnum.Pay_Num]
        )
      );
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [policies, selectedPolicies]);

  const handleSelectAll = (isChecked: boolean) => {
    setSelectAllChecked(isChecked);
    setSelectedPolicies(
      isChecked
        ? policies.map((policy) => [
            policy[PolicyReportMappingEnum.Pol_ID],
            policy[PolicyReportMappingEnum.Pay_Num],
          ])
        : []
    );
  };

  const requestSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setPendingSelectAll(true);
      return;
    }
    handleSelectAll(isChecked);
  };

  const confirmSelectAll = () => {
    setPendingSelectAll(false);
    handleSelectAll(true);
  };

  const cancelSelectAll = () => {
    setPendingSelectAll(false);
  };

  const handleRowSelection = (policy: any, isChecked: boolean) => {
    setSelectedPolicies((prev) => {
      const updated = [...prev];
      const entry: [string, number] = [
        policy[PolicyReportMappingEnum.Pol_ID],
        policy[PolicyReportMappingEnum.Pay_Num],
      ];
      const index = updated.findIndex(
        ([polId, payNum]) => polId === entry[0] && payNum === entry[1]
      );

      if (isChecked && index === -1) {
        updated.push(entry);
      } else if (!isChecked && index !== -1) {
        updated.splice(index, 1);
      }

      return updated;
    });
  };

  const columns = [
    {
      name: "selected",
      header: (
        <div className="flex w-full items-center justify-center">
          <input
            type="checkbox"
            checked={selectAllChecked}
            onChange={(e) => requestSelectAll(e.target.checked)}
          />
        </div>
      ),
      render: ({ data }) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={selectedPolicies.some(
              ([polId, payNum]) =>
                polId === data[PolicyReportMappingEnum.Pol_ID] &&
                payNum === data[PolicyReportMappingEnum.Pay_Num]
            )}
            onChange={(e) => handleRowSelection(data, e.target.checked)}
          />
        </div>
      ),
      type: "checkbox",
      sortable: false,
      filterable: false,
      resizable: false,
      enableColumnFilterContextMenu: false,
      maxWidth: 50,
      minWidth: 50,
    },
    {
      name: PolicyReportMappingEnum.DATE_ND,
      header: "Дата на Вноска",
      type: "string",
      sort: (a: string, b: string) => {
        const aDate = moment(a, "DD/MM/YYYY", true).valueOf();
        const bDate = moment(b, "DD/MM/YYYY", true).valueOf();
        return aDate - bDate;
      },
    },
    {
      name: PolicyReportMappingEnum.Pol_Kind,
      header: "Вид Полица",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.Pol_Num,
      header: "№ Полица",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.Pay_Num,
      header: "Вид Полица",
      type: "string",
      render: ({ data }) => {
        const installmentNumber = Number(data[PolicyReportMappingEnum.Pay_Num]);

        return installmentNumber === 1 ? (
          <span>Договор</span>
        ) : (
          <span>
            Вноска № <span className="font-bold">{installmentNumber}</span>
          </span>
        );
      },
    },
    {
      name: PolicyReportMappingEnum.Total_Sum,
      header: "Премия",
      type: "number",
    },
    {
      name: PolicyReportMappingEnum.Sum_all,
      header: "Сума полица",
      type: "number",
    },

    {
      name: PolicyReportMappingEnum.Sum_Stk,
      header: "Стикер Сума",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.Sum_GF,
      header: "ГФ",
      type: "number",
    },
    {
      name: PolicyReportMappingEnum.Sum_2Perc,
      header: "2%",
      type: "number",
    },
    {
      name: PolicyReportMappingEnum.Commission,
      header: "К-на %",
      type: "number",
    },
    {
      name: PolicyReportMappingEnum.Sum_Comm,
      header: "Сума К-на",
      type: "number",
    },
    {
      name: PolicyReportMappingEnum.Sticker,
      header: "Стикер",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.Green_Card,
      header: "СЗК",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.Pay_Off,
      header: "Място Плащане",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.EGN,
      header: "ЕГН",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.CL_NAME,
      header: "Имена",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.InsComp,
      header: "ЗК",
      type: "string",
    },
    {
      name: PolicyReportMappingEnum.Office,
      header: "Офис",
      type: "string",
    },
  ];

  const handleExportCSV = () => {
    const lines: string[] = [];

    // ── Determine rows to export
    const selectedRows = policies.filter((policy) =>
      selectedPolicies.some(
        ([polId, payNum]) =>
          polId === policy[PolicyReportMappingEnum.Pol_ID] &&
          payNum === policy[PolicyReportMappingEnum.Pay_Num]
      )
    );
    const rowsToExport = selectedRows.length > 0 ? selectedRows : policies;

    // ── Section 1: Filter parameters ──────────────────────────────
    lines.push("ПАРАМЕТРИ НА СПРАВКАТА" + SEPARATOR);
    lines.push(`От дата${SEPARATOR}${formState?.fromDate ?? ""}`);
    lines.push(`До дата${SEPARATOR}${formState?.toDate ?? ""}`);
    lines.push(
      `ЗК${SEPARATOR}${
        labelMaps?.companies[formState?.company] ?? formState?.company ?? ""
      }`
    );
    lines.push(
      `Вид Полица${SEPARATOR}${
        labelMaps?.policyTypes[formState?.policyType] ??
        formState?.policyType ??
        ""
      }`
    );
    lines.push(
      `Агент${SEPARATOR}${
        labelMaps?.agents[formState?.agent] ?? formState?.agent ?? ""
      }`
    );
    lines.push(
      `Офис${SEPARATOR}${(formState?.selectedOffices ?? [])
        .map((id) => labelMaps?.offices[id] ?? id)
        .join("; ")}`
    );
    lines.push(
      `Място на плащане${SEPARATOR}${
        labelMaps?.paymentPlaces[formState?.paymentPlace] ??
        formState?.paymentPlace ??
        ""
      }`
    );
    lines.push(
      `Вкл. анулирани${SEPARATOR}${
        formState?.shouldIncludeAnnulledPolicies === 1 ? "Да" : "Не"
      }`
    );
    lines.push(`Номер на отчет${SEPARATOR}${formState?.reportNumber ?? ""}`);
    lines.push(`Дата на отчет${SEPARATOR}${formState?.reportDate ?? ""}`);
    lines.push("");

    // ── Section 2: Policy data ─────────────────────────────────────
    const exportColumns = columns.filter((c) => c.name !== "selected");
    lines.push("ПОЛИЦИ" + SEPARATOR);
    lines.push(
      ["№", ...exportColumns.map((c) => c.header as string)].join(SEPARATOR)
    );

    rowsToExport.forEach((policy, index) => {
      const row = [
        index + 1,
        ...exportColumns.map((c) => {
          const val = policy[c.name] ?? "";

          if (c.name === PolicyReportMappingEnum.Pay_Num) {
            const n = Number(val);
            return n === 1 ? "Договор" : `Вноска №${n}`;
          }

          const str = String(val);
          return str.includes(SEPARATOR) || str.includes("\n")
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        }),
      ].join(SEPARATOR);
      lines.push(row);
    });

    lines.push("");

    // ── Section 3: Totals ──────────────────────────────────────────
    if (totals) {
      lines.push("ОБОБЩЕНИЕ" + SEPARATOR);
      lines.push(
        `Обща сума на премиите${SEPARATOR}${totals.totalPremiumAmount}`
      );
      lines.push(`Общ комисион${SEPARATOR}${totals.totalCommissionAmount}`);
      lines.push(`Сума за отчет${SEPARATOR}${totals.reportSum}`);
    }

    const blob = new Blob(["\uFEFF" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Отчет-ЗК-${moment().format("DD-MM-YYYY")}`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <DataGrid
        columns={columns}
        dataSource={policies}
        isLoading={isLoading}
        gridTitle="Справка отчет към ЗК"
        idProperty={PolicyReportMappingEnum.Pol_ID}
        csvFilename={`Отчет-ЗК-${moment().format("DD-MM-YYYY")}`}
        editable={false}
        onExportCSV={handleExportCSV}
        getPrintRows={(filteredRows) => {
          const selectedRows = policies.filter((policy) =>
            selectedPolicies.some(
              ([polId, payNum]) =>
                polId === policy[PolicyReportMappingEnum.Pol_ID] &&
                payNum === policy[PolicyReportMappingEnum.Pay_Num]
            )
          );

          if (selectedRows.length > 0) {
            return selectedRows;
          }

          if (filteredRows.length > 0) {
            return filteredRows;
          }

          return policies;
        }}
        printReportMetadata={printReportMetadata}
        printReportFooterMetadata={printReportFooterMetadata}
      />

      {pendingSelectAll && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9998,
            }}
            onClick={cancelSelectAll}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#fff",
              borderRadius: 12,
              padding: "28px 28px 20px",
              zIndex: 9999,
              width: 380,
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
              textAlign: "center" as const,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#fff1ee",
                border: "2px solid #f5d0c5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ fontSize: 22, color: "#8a1d06" }}
              />
            </div>

            <h3 className="mb-2 text-sm font-bold text-black">
              Потвърждение на избор
            </h3>

            <p className="mb-6 text-xs font-medium leading-relaxed text-gray-600">
              Сигурни ли сте, че искате да изберете{" "}
              <span className="font-bold text-black">всички</span>?
            </p>

            <div className="flex justify-center gap-3">
              <button
                className="rounded-md border border-gray-300 px-5 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50"
                onClick={cancelSelectAll}
              >
                Отказ
              </button>
              <button
                className="flex items-center gap-2 rounded-md px-5 py-2 text-xs font-bold text-white transition hover:opacity-90"
                style={{ backgroundColor: "#8a1d06" }}
                onClick={confirmSelectAll}
              >
                <i className="fa-solid fa-check" />
                Да, избери всички
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PoliciesReportDataGrid;
